import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  MenuItem,
} from '@material-ui/core';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { Formik, Form, FormikHelpers, yupToFormErrors } from 'formik';
import {
  GrantType,
  GrantTypeNames,
  ExternalAuthConfig,
  getDefaultExternalAuth,
} from '../types';
import { ExternalAuthModalProps, SettingsFieldsProps } from './types';
import { GRANT_TYPE_OPTIONS, defaultSettingsByGrantType } from './constants';
import { getValidationSchema } from './validation';
import AuthorizationCodeFields from './AuthorizationCodeFields';
import ClientCredentialsFields from './ClientCredentialsFields';
import EmailCredentialsFields from './EmailCredentialsFields';
import JwtCertificateFields from './JwtCertificateFields';

const settingsFieldsByGrantType: Partial<
  Record<GrantType, React.FC<SettingsFieldsProps>>
> = {
  [GrantType.AuthorizationCode]: AuthorizationCodeFields,
  [GrantType.ClientCredentials]: ClientCredentialsFields,
  [GrantType.EmailCredentials]: EmailCredentialsFields,
  [GrantType.JwtCertificate]: JwtCertificateFields,
};

const ExternalAuthModal: React.FC<ExternalAuthModalProps> = ({
  open,
  onClose,
  orgId,
  id,
}) => {
  const title = id ? 'Edit External Auth' : 'Create External Auth';

  const externalAuthAction = useStoreActions(
    (actions: any) => actions.externalAuth,
  );
  const commonAction = useStoreActions((actions: any) => actions.common);
  const currentExternalAuth: ExternalAuthConfig = useStoreState(
    (state: any) =>
      state.externalAuth.currentExternalAuth ?? getDefaultExternalAuth(),
  );

  useEffect(() => {
    if (open) {
      externalAuthAction.getExternalAuthById({ orgId, id });
    }
    return () => {
      externalAuthAction.setCurrentExternalAuth(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, open, orgId]);

  const handleSubmit = async (
    values: ExternalAuthConfig,
    { setSubmitting }: FormikHelpers<ExternalAuthConfig>,
  ) => {
    try {
      await externalAuthAction.saveExternalAuth({
        orgId,
        data: values,
        onSuccess: () => {
          externalAuthAction.getExternalAuthList({ orgId });
          onClose();
        },
        commonAction,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
      PaperProps={{ style: { borderRadius: 20 } }}
      open={open}
    >
      <Box>
        <Formik
          initialValues={currentExternalAuth}
          validate={(values: ExternalAuthConfig) => {
            try {
              getValidationSchema(values.grantTypeId).validateSync(values, {
                abortEarly: false,
              });
              return {};
            } catch (err) {
              return yupToFormErrors(err);
            }
          }}
          validateOnChange
          validateOnBlur
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values, errors, handleChange, setFieldValue }) => {
            const se = (errors.settings ?? {}) as Record<string, string>;
            const s = values.settings as any;
            const SettingsFields = settingsFieldsByGrantType[values.grantTypeId];
            return (
              <Form>
                <DialogContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" component="h6">
                        {title}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        label="Auth Name"
                        variant="outlined"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        name="grantTypeId"
                        value={values.grantTypeId}
                        onChange={(e) => {
                          const next = Number(e.target.value) as GrantType;
                          setFieldValue('grantTypeId', next);
                          setFieldValue(
                            'settings',
                            defaultSettingsByGrantType[next] ?? {},
                          );
                        }}
                        label="Grant Type"
                        variant="outlined"
                        disabled={!!values.id}
                      >
                        {GRANT_TYPE_OPTIONS.map((v) => (
                          <MenuItem key={v} value={v}>
                            {GrantTypeNames[v]}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {values.grantTypeId !== GrantType.JwtCertificate && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="settings.tokenUri"
                          value={s.tokenUri ?? ''}
                          onChange={handleChange}
                          error={!!se.tokenUri}
                          helperText={se.tokenUri}
                          label="Token URI"
                          variant="outlined"
                        />
                      </Grid>
                    )}

                    {SettingsFields && (
                      <SettingsFields
                        s={s}
                        se={se}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                      />
                    )}
                  </Grid>

                  <Grid
                    container
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                    style={{ marginTop: 20 }}
                  >
                    <Grid item>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={onClose}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button type="submit" variant="contained" color="primary">
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </DialogContent>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Dialog>
  );
};

export default ExternalAuthModal;
